// ============================================================================
// Contabilità (Accounting) — data layer (real FastAPI + MongoDB backend, Phase 4)
// ----------------------------------------------------------------------------
// Read = view_accounting (manager+), write = edit_accounting (admin+). Store
// backend-hydrated + mutazioni ottimistiche. KPI/insight/profittabilità restano
// derivati client-side da contratti (Administration) + ore Monthly Ops (nessuna
// duplicazione di sorgente). NO email/fattura/Fatture in Cloud/TS Pay/banche reali.
// ============================================================================

import { getActiveRegistryClients, getRegistryClient } from "@/lib/clientRegistry";
import { getContractByClient, getContracts, getProposals, gross } from "@/lib/administrationData";
import { productMonthly, contractMonthly as sharedContractMonthly } from "@/lib/contractMath";
import { listOps, opTotalTime, MONTHS_IT } from "@/lib/tasksData";
import { api } from "@/lib/api";
import { toast } from "sonner";

const _now = new Date();
export const REV_YEAR = _now.getFullYear();
export const CUR_MONTH = _now.getMonth() + 1;
export const HOURLY_COST = 25;
export { MONTHS_IT };

const now = () => new Date().toISOString();
const uid = (p) => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export const PAYMENT_STATUS = {
  paid: { label: "Pagato", tone: "ok" }, pending: { label: "In sospeso", tone: "info" },
  unpaid: { label: "Non pagato", tone: "warning" }, overdue: { label: "Scaduto", tone: "error" },
};
export const INVOICE_STATUS = {
  to_issue: { label: "Da generare", tone: "neutral" }, pending: { label: "In attesa", tone: "info" }, issued: { label: "Emessa", tone: "ok" },
};
export const PAYMENT_METHODS = ["Bonifico bancario", "Addebito diretto (RID)", "Pagamento su fattura", "Altro"];
export const WA_CHARGED = ["Agenzia", "Cliente", "Incluso nel piano", "Non applicabile"];
export const COST_CATEGORIES = ["Software", "Advertising Tool", "WhatsApp", "Hosting", "Freelancer", "Costo cliente", "Personale", "Altro"];
export const COST_RECURRENCE = ["Mensile", "Trimestrale", "Semestrale", "Annuale", "Personalizzata"];
export const COST_PAYMENT_METHODS = ["Bonifico", "Addebito diretto Carta", "Addebito diretto su c/c", "Altro"];
export const OCC_SOURCES = { manual: "Manuale", tasks_extra_cost: "Tasks Extra Cost", whatsapp: "WhatsApp", other: "Altro" };
export const EMAIL_CATEGORIES = {
  payment_reminder: "Sollecito pagamento", payment_received: "Pagamento ricevuto", proposal_sent: "Proposta inviata",
  proposal_accepted: "Proposta accettata", contract_renewal: "Rinnovo contratto", contract_expiring: "Contratto in scadenza",
};
export const TEMPLATE_VARS = ["{{client_name}}", "{{company_name}}", "{{amount_due}}", "{{payment_month}}", "{{due_date}}", "{{proposal_link}}", "{{contract_end}}", "{{sender_name}}"];
export const SUB_RENEWALS = ["Mensile", "Annuale"];
export const SUB_TYPES = ["WhatsApp", "CRM", "Hosting", "Dominio", "Licenza", "Altro"];
export const SUB_PERIODS = ["Mensile", "Trimestrale", "Annuale"];
export const SUB_STATUSES = ["Attivo", "In scadenza", "Terminato"];
export const createSubscription = (data) => {
  const id = uid("sub");
  const row = { id, status: "Attivo", net_amount: 0, ...data };
  _store.subscriptions = [...(_store.subscriptions || []), row]; touch();
  persist(api.post("/accounting/subscriptions", { id, ...data }));
  return id;
};
export const subEffectiveStatus = (s) => {
  if (s.status === "Terminato") return "Terminato";
  const iso = s.due_date || s.renewal_date;
  if (iso) { const days = (new Date(iso) - new Date()) / 86400000; if (days <= 30) return "In scadenza"; }
  return s.status || "Attivo";
};

// ---------------------------------------------------------------------------
// CACHE (backend-hydrated)
// ---------------------------------------------------------------------------
const defaultSubOptions = () => ({ paidBy: ["Carta Aesthetic", "Metodo cliente"], platforms: ["Stripe", "Addebito CC", "Fattura", "Bonifico", "PayPal"] });
let _store = { revenue: [], reminders: [], templates: [], costs: [], subscriptions: [], subOptions: defaultSubOptions(), hourlyRates: {} };
// I costi extra delle Operazioni mensili sono costi occasionali veri: ricarica quando cambiano.
try { window.addEventListener("aeos-extra-costs", () => { if (_hydrated) { _hydrated = false; hydrateAccounting(); } }); } catch { /* noop */ }
let _hydrated = false;
let _hydratingPromise = null;
const _subs = new Set();
const notify = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
export const subscribeAccounting = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const isAccountingReady = () => _hydrated;

export async function hydrateAccounting() {
  if (_hydrated) return;
  if (_hydratingPromise) return _hydratingPromise;
  _hydratingPromise = (async () => {
    try {
      const { data } = await api.get("/accounting/store");
      _store = { revenue: [], reminders: [], templates: [], costs: [], subscriptions: [], subOptions: defaultSubOptions(), hourlyRates: {}, ...data };
      // Normalizza righe importate: garantisce sempre items[] (le righe da migrazione non lo hanno)
      _store.revenue = (_store.revenue || []).map((r) => ({ ...r, items: Array.isArray(r.items) ? r.items : [] }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("hydrateAccounting failed", e?.response?.status);
    } finally { _hydrated = true; _hydratingPromise = null; notify(); }
  })();
  return _hydratingPromise;
}
export const resetAccounting = () => { _store = { revenue: [], reminders: [], templates: [], costs: [], subscriptions: [], subOptions: defaultSubOptions(), hourlyRates: {} }; _hydrated = false; _hydratingPromise = null; };
const resync = () => { _hydrated = false; hydrateAccounting(); };
const read = () => _store;
const touch = () => { _store = { ..._store }; notify(); };
const persist = (promise) => { promise.catch(() => { toast.error("Salvataggio non riuscito: dati ricaricati"); resync(); }); };

// ---------------------------------------------------------------------------
// ABBONAMENTI
// ---------------------------------------------------------------------------
export const getSubscriptions = () => read().subscriptions || [];
export const getSubscriptionsByClient = (clientId) => (read().subscriptions || []).filter((s) => s.client_id === clientId);
export const addSubscription = (clientId) => {
  const id = uid("sub");
  _store.subscriptions = [...(_store.subscriptions || []), { id, client_id: clientId, provider: "", description: "", amount: 0, renewal_type: "Annuale", due_date: "", paid_by: "Carta Aesthetic", platform: "Stripe" }]; touch();
  persist(api.post("/accounting/subscriptions", { id, client_id: clientId, provider: "", description: "", amount: 0, renewal_type: "Annuale", due_date: "", paid_by: "Carta Aesthetic", platform: "Stripe" }));
  return id;
};
export const updateSubscription = (id, p) => { _store.subscriptions = (_store.subscriptions || []).map((x) => (x.id === id ? { ...x, ...p } : x)); touch(); persist(api.patch(`/accounting/subscriptions/${id}`, p)); };
export const removeSubscription = (id) => { _store.subscriptions = (_store.subscriptions || []).filter((x) => x.id !== id); touch(); persist(api.delete(`/accounting/subscriptions/${id}`)); };

export const getSubOptions = () => read().subOptions || defaultSubOptions();
export const addSubOption = (type, val) => { const v = (val || "").trim(); if (!v) return; const opts = { ...(_store.subOptions || defaultSubOptions()) }; if (!opts[type].includes(v)) opts[type] = [...opts[type], v]; _store.subOptions = opts; touch(); persist(api.patch("/accounting/settings", { subOptions: opts })); };
export const removeSubOption = (type, val) => { const opts = { ...(_store.subOptions || defaultSubOptions()) }; opts[type] = opts[type].filter((x) => x !== val); _store.subOptions = opts; touch(); persist(api.patch("/accounting/settings", { subOptions: opts })); };

export const subscriptionsDueInMonth = (year, month) => (read().subscriptions || []).filter((s) => {
  if (!s.due_date) return false; const d = new Date(s.due_date); return d.getFullYear() === year && d.getMonth() + 1 === month;
});

// ---------------------------------------------------------------------------
// REVENUE
// ---------------------------------------------------------------------------
export const getRevenue = (year, month) => read().revenue.filter((r) => r.year === year && r.month === month);
export const getRevenueByClient = (clientId, year) => read().revenue.filter((r) => r.client_id === clientId && (!year || r.year === year)).sort((a, b) => a.month - b.month);
const recalc = (items) => { const net = items.reduce((a, x) => a + (Number(x.net_amount) || 0), 0); return { net_amount: net, gross_amount: gross(net) }; };
const localRev = (id, p) => { _store.revenue = _store.revenue.map((x) => (x.id === id ? { ...x, ...p } : x)); touch(); };
// Righe importate da Fatture in Cloud/insoluti = totale fiscale storico immutabile.
export const isFiscalLocked = (r) => !!(r && (r.source || r.fiscal_locked));
export const itemsNet = (r) => (r?.items || []).reduce((a, x) => a + (Number(x.net_amount) || 0), 0);
export const updateRevenue = (id, p) => { localRev(id, p); persist(api.patch(`/accounting/revenue/${id}`, p)); };
export const markPaid = (id) => updateRevenue(id, { payment_status: "paid" });
export const markInvoiceIssued = (id) => updateRevenue(id, { invoice_status: "issued", invoice_issued: true });
export const setRevenueItems = (id, items) => {
  const r = _store.revenue.find((x) => x.id === id);
  if (isFiscalLocked(r)) { localRev(id, { items }); persist(api.patch(`/accounting/revenue/${id}`, { items })); return; }
  const t = recalc(items); localRev(id, { items, ...t }); persist(api.patch(`/accounting/revenue/${id}`, { items }));
};
export const addRevenueRow = (clientId, month, year) => {
  const id = uid("mr");
  const row = { id, client_id: clientId, month, year, net_amount: 0, gross_amount: 0, invoice_issued: false, invoice_status: "to_issue", payment_status: "pending", payment_method: PAYMENT_METHODS[0], whatsapp_subscription_active: false, whatsapp_charged_to: "Non applicabile", reminder_status: "Non inviato", reminder_count: 0, last_reminder_at: null, items: [] };
  _store.revenue = [..._store.revenue, row]; touch();
  persist(api.post("/accounting/revenue", { id, client_id: clientId, month, year, items: [] }));
  return id;
};
export const duplicateRevenue = (id) => {
  const r = _store.revenue.find((x) => x.id === id); if (!r) return;
  const nid = uid("mr");
  _store.revenue = [..._store.revenue, { ...r, id: nid, items: r.items.map((it) => ({ ...it, id: uid("ri") })) }]; touch();
  persist(api.post(`/accounting/revenue/${id}/duplicate`).then(({ data }) => { _store.revenue = _store.revenue.map((x) => (x.id === nid ? data : x)); touch(); }));
};
export const moveRevenue = (id, month) => updateRevenue(id, { month: Number(month) });
export const removeRevenue = (id) => { _store.revenue = _store.revenue.filter((x) => x.id !== id); touch(); persist(api.delete(`/accounting/revenue/${id}`)); };

// REMINDERS
export const getReminders = () => read().reminders.slice().sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
export const sendReminder = (rev, { subject, body, sentBy, sentByEmail }) => {
  const count = (rev.reminder_count || 0) + 1;
  localRev(rev.id, { reminder_status: count === 1 ? "Inviato 1 volta" : `Inviato ${count} volte`, reminder_count: count, last_reminder_at: now() });
  const payload = { client_id: rev.client_id, monthly_revenue_id: rev.id, amount_due: rev.gross_amount, payment_month: `${MONTHS_IT[rev.month - 1]} ${rev.year}`, sent_by_email: sentByEmail || "team@aesthetic.os", sent_to_email: getRegistryClient(rev.client_id)?.main_contact_email, email_subject: subject, email_body: body };
  _store.reminders = [{ id: uid("rem"), ...payload, status: "Inviato", sent_at: now(), created_at: now() }, ..._store.reminders]; touch();
  persist(api.post("/accounting/reminders", payload).then(({ data }) => { hydrateAccounting(); return data; }));
};
export const markReminderPaid = (reminderId) => {
  const r = _store.reminders.find((x) => x.id === reminderId);
  if (r?.monthly_revenue_id) localRev(r.monthly_revenue_id, { payment_status: "paid" });
  _store.reminders = _store.reminders.map((x) => (x.id === reminderId ? { ...x, status: "Pagato" } : x)); touch();
  persist(api.post(`/accounting/reminders/${reminderId}/paid`));
};

// TEMPLATES
export const getEmailTemplates = () => read().templates;
export const addEmailTemplate = (d) => { const id = uid("et"); _store.templates = [..._store.templates, { id, is_active: true, created_by: "Operatore", updated_at: now(), ...d }]; touch(); persist(api.post("/accounting/templates", { id, ...d }).then(({ data }) => { _store.templates = _store.templates.map((x) => (x.id === id ? data : x)); touch(); })); };
export const updateEmailTemplate = (id, p) => { _store.templates = _store.templates.map((x) => (x.id === id ? { ...x, ...p, updated_at: now() } : x)); touch(); persist(api.patch(`/accounting/templates/${id}`, p)); };
export const removeEmailTemplate = (id) => { _store.templates = _store.templates.filter((x) => x.id !== id); touch(); persist(api.delete(`/accounting/templates/${id}`)); };
export const duplicateEmailTemplate = (id) => { const t = _store.templates.find((x) => x.id === id); if (!t) return; const nid = uid("et"); _store.templates = [..._store.templates, { ...t, id: nid, template_name: `${t.template_name} (copia)`, updated_at: now() }]; touch(); persist(api.post(`/accounting/templates/${id}/duplicate`).then(({ data }) => { _store.templates = _store.templates.map((x) => (x.id === nid ? data : x)); touch(); })); };

// COSTS
export const getRecurringCosts = () => read().costs.filter((c) => c.cost_type === "recurring");
// I costi extra per-cliente (Operazioni mensili) sono salvati come costi occasionali reali (source tasks_extra_cost).
export const getExtraCostsAsOccasional = () => [];
export const getOccasionalCosts = (year, month) => {
  return read().costs.filter((c) => c.cost_type === "occasional" && c.status !== "ended" && (!year || c.year === year) && (!month || c.month === month));
};
export const addCost = (d) => { const id = uid("cost"); _store.costs = [..._store.costs, { id, status: "active", source: "manual", ...d }]; touch(); persist(api.post("/accounting/costs", { id, ...d }).then(({ data }) => { _store.costs = _store.costs.map((x) => (x.id === id ? data : x)); touch(); })); return id; };
export const updateCost = (id, p) => { _store.costs = _store.costs.map((x) => (x.id === id ? { ...x, ...p } : x)); touch(); persist(api.patch(`/accounting/costs/${id}`, p)); };
export const removeCost = (id) => { _store.costs = _store.costs.filter((x) => x.id !== id); touch(); persist(api.delete(`/accounting/costs/${id}`)); };
export const duplicateCost = (id) => {
  const src = _store.costs.find((x) => x.id === id); if (!src) return null;
  const nid = uid("cost");
  // eslint-disable-next-line no-unused-vars
  const { id: _srcId, ...rest } = src;
  const copy = { ...rest, id: nid, cost_name: `${src.cost_name || "Costo"} (copia)` };
  _store.costs = [..._store.costs, copy]; touch();
  persist(api.post("/accounting/costs", { id: nid, ...rest, cost_name: copy.cost_name }).then(({ data }) => { _store.costs = _store.costs.map((x) => (x.id === nid ? data : x)); touch(); }));
  return nid;
};
export const toggleCostPause = (id) => { _store.costs = _store.costs.map((x) => (x.id === id ? { ...x, status: x.status === "active" ? "paused" : "active" } : x)); touch(); persist(api.post(`/accounting/costs/${id}/toggle`)); };
export const costCategoryLabel = (c) => (c?.category === "Altro" && (c.category_custom || "").trim()) ? c.category_custom.trim() : (c?.category || "—");

const monthKey = (y, m) => y * 100 + m;
const seedVersion = (c) => ({ effective_year: c.start_year || REV_YEAR, effective_month: c.start_month || 1, amount: c.amount || 0, payment_method: c.payment_method || "Bonifico", payment_method_custom: c.payment_method_custom || "" });
export const costVersionFor = (cost, year, month) => {
  const vs = (cost.versions && cost.versions.length) ? cost.versions : [seedVersion(cost)];
  const elig = vs.filter((v) => monthKey(v.effective_year, v.effective_month) <= monthKey(year, month));
  // Prima del primo "valido dal": importo iniziale (stessa regola del backend).
  if (!elig.length) return [...vs].sort((a, b) => monthKey(a.effective_year, a.effective_month) - monthKey(b.effective_year, b.effective_month))[0];
  return [...elig].sort((a, b) => monthKey(b.effective_year, b.effective_month) - monthKey(a.effective_year, a.effective_month))[0];
};
export const costAmountFor = (cost, year, month) => Number(costVersionFor(cost, year, month).amount) || 0;
export const costPaymentFor = (cost, year, month) => { const v = costVersionFor(cost, year, month); return v.payment_method === "Altro" && v.payment_method_custom ? v.payment_method_custom : (v.payment_method || "—"); };
export const setRecurringVersion = (id, { year, month, amount, payment_method, payment_method_custom }) => {
  const c = _store.costs.find((x) => x.id === id); if (!c) return;
  const versions = (c.versions && c.versions.length) ? [...c.versions] : [seedVersion(c)];
  const v = { effective_year: year, effective_month: month, amount: Number(amount) || 0, payment_method: payment_method || "Bonifico", payment_method_custom: payment_method_custom || "" };
  const idx = versions.findIndex((x) => x.effective_year === year && x.effective_month === month);
  if (idx > -1) versions[idx] = v; else versions.push(v);
  versions.sort((a, b) => monthKey(a.effective_year, a.effective_month) - monthKey(b.effective_year, b.effective_month));
  const latest = versions[versions.length - 1];
  _store.costs = _store.costs.map((x) => (x.id === id ? { ...c, versions, amount: latest.amount, payment_method: latest.payment_method, payment_method_custom: latest.payment_method_custom } : x)); touch();
  persist(api.post(`/accounting/costs/${id}/version`, { year, month, amount, payment_method, payment_method_custom }));
};

// Stessa logica del backend (core/costs.py in_month): ricorrenza, anno di inizio, fine, pausa.
const COST_FREQ = { Mensile: 1, Trimestrale: 3, Semestrale: 6, Annuale: 12 };
const recurringActiveIn = (c, month, year = REV_YEAR) => {
  if (c.deleted_at || c.status === "paused") return false;
  const every = Math.max(1, Number(c.recurrence === "Personalizzata" ? c.custom_every_months : (COST_FREQ[c.recurrence] || 1)) || 1);
  const sy = c.start_year || c.year || year;
  const sm = c.start_month || c.month || 1;
  if (year * 100 + month < sy * 100 + sm) return false;
  if (c.recurring_until && `${year}-${String(month).padStart(2, "0")}` > String(c.recurring_until).slice(0, 7)) return false;
  if (c.end_month && month > Number(c.end_month)) return false;
  return ((year * 12 + month) - (sy * 12 + sm)) % every === 0;
};
export const recurringCostsForMonth = (month, year = REV_YEAR) => getRecurringCosts().filter((c) => recurringActiveIn(c, month, year));

export const costSummary = (year, month) => {
  const realRec = recurringCostsForMonth(month, year);
  // Il costo del lavoro NON è più calcolato in automatico: si inserisce a mano come costo ricorrente.
  const rec = realRec.reduce((a, c) => a + costAmountFor(c, year, month), 0);
  const occList = getOccasionalCosts(year, month);
  const occ = occList.reduce((a, c) => a + c.amount, 0);
  const recAmt = (c) => costAmountFor(c, year, month);
  const client = realRec.filter((c) => c.assigned_to_type === "client").reduce((a, c) => a + recAmt(c), 0) + occList.filter((c) => c.assigned_to_type === "client").reduce((a, c) => a + c.amount, 0);
  const agency = realRec.filter((c) => c.assigned_to_type === "agency").reduce((a, c) => a + recAmt(c), 0) + occList.filter((c) => c.assigned_to_type === "agency").reduce((a, c) => a + c.amount, 0);
  return { recurring: rec, occasional: occ, total: rec + occ, client, agency, labor: 0 };
};

export const monthlySeries = (year) => MONTHS_IT.map((mn, i) => {
  const m = i + 1;
  const rows = getRevenue(year, m);
  const net = rows.reduce((a, r) => a + r.net_amount, 0);
  const grossV = rows.reduce((a, r) => a + r.gross_amount, 0);
  return { month: mn.slice(0, 3), lordo: grossV, ricavi: net, benchmark: costSummary(year, m).total };
});

// ---------------------------------------------------------------------------
// KPI / INSIGHTS / PROFITABILITY / FORECAST
// ---------------------------------------------------------------------------
const clientHours = (clientId) => { const op = listOps(REV_YEAR, CUR_MONTH).find((o) => o.client_id === clientId); return op ? Math.max(opTotalTime(op), 1) : 8; };
export const getHourlyRates = () => read().hourlyRates || {};
export const setHourlyRate = (userId, rate) => { const rates = { ...(_store.hourlyRates || {}), [userId]: Number(rate) || 0 }; _store.hourlyRates = rates; touch(); persist(api.patch("/accounting/settings", { hourlyRates: rates })); };
export const avgHourlyRate = () => { const v = Object.values(getHourlyRates()); return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : HOURLY_COST; };
export const teamLaborMonthly = () => { const rate = avgHourlyRate(); return Math.round(getActiveRegistryClients().reduce((a, c) => a + clientHours(c.id) * rate, 0)); };
export const acceptedProposalsTotal = () => getProposals().filter((p) => p.status === "accepted").reduce((a, p) => a + (p.amount || 0), 0);

// Ricorrente mensile = somma delle mensilità dei contratti attivi (fonte unica: Preventivi e Contratti).
const recurringMonthly = () => getContracts().filter((ct) => !ct.deleted_at && !["terminated", "cancelled"].includes(ct.status))
  .reduce((a, ct) => a + sharedContractMonthly(ct).monthlyNet, 0);

export const accountingKpis = () => {
  const s = read();
  const yearRows = s.revenue.filter((r) => r.year === REV_YEAR);
  const netYear = yearRows.reduce((a, r) => a + r.net_amount, 0);
  const grossYear = yearRows.reduce((a, r) => a + r.gross_amount, 0);
  const curMonth = getRevenue(REV_YEAR, CUR_MONTH).reduce((a, r) => a + r.net_amount, 0);
  const pending = yearRows.filter((r) => r.payment_status === "pending").reduce((a, r) => a + r.gross_amount, 0);
  const unpaid = yearRows.filter((r) => ["unpaid", "overdue"].includes(r.payment_status)).reduce((a, r) => a + r.gross_amount, 0);
  const recurring = recurringMonthly();
  const costsMonth = costSummary(REV_YEAR, CUR_MONTH).total;
  return { netYear, grossYear, curMonth, forecast: forecastNextMonth(), pending, unpaid, recurring, costsMonth };
};

// ---------------------------------------------------------------------------
// CARD DASHBOARD CONTABILITÀ (solo dati canonici AOS — mai dati Fatture in Cloud)
// ---------------------------------------------------------------------------
export const FIC_UI_STATUS = {
  da_generare: { label: "Da generare", tone: "neutral" },
  generazione_in_corso: { label: "Generazione in corso", tone: "info" },
  emessa_da_inviare: { label: "Emessa · da inviare allo SDI", tone: "warning" },
  inviata_sdi: { label: "Inviata allo SDI", tone: "ok" },
  errore: { label: "Errore", tone: "error" },
  errore_sdi: { label: "Errore SDI", tone: "error" },
  nota_credito: { label: "Nota di credito presente", tone: "info" },
  parzialmente_stornata: { label: "Parzialmente stornata", tone: "warning" },
  stornata: { label: "Stornata", tone: "error" },
};

export const monthTotals = (year, month) => {
  const rows = getRevenue(year, month);
  return {
    net: rows.reduce((a, r) => a + (Number(r.net_amount) || 0), 0),
    gross: rows.reduce((a, r) => a + (Number(r.gross_amount) || 0), 0),
  };
};

// Insoluti = esclusivamente crediti scaduti e non incassati risultanti in AOS.
export const insolutiTotal = () => read().revenue
  .filter((r) => ["unpaid", "overdue"].includes(r.payment_status))
  .reduce((a, r) => a + (Number(r.gross_amount) || 0), 0);

const INACTIVE_STATUSES = ["paused", "suspended", "terminated", "cancelled", "archived"];

// Previsione mese prossimo: somma delle mensilità dei contratti attivi nel mese successivo
// (senza doppio conteggio: se il cliente ha già un contratto attivo, il preventivo non si somma).
export const forecastNextMonth = (baseYear, baseMonth) => {
  let y = baseYear ?? REV_YEAR; let m = (baseMonth ?? CUR_MONTH) + 1;
  if (m > 12) { m = 1; y += 1; }
  const startNext = `${y}-${String(m).padStart(2, "0")}-01`;
  const endNext = `${y}-${String(m).padStart(2, "0")}-31`;
  const rows = [];
  // Solo contratti: somma delle mensilità dei prodotti attivi nel mese successivo (funzione condivisa).
  getContracts().forEach((ct) => {
    if (ct.deleted_at || ["terminated", "cancelled"].includes(ct.status)) return;
    const c = getRegistryClient(ct.client_id);
    if (c && INACTIVE_STATUSES.includes(c.status)) return;
    let amount = 0;
    (ct.included_products || []).forEach((p) => {
      if ((p.status || "active") !== "active") return;
      if ((p.quantity === null || p.quantity === undefined || p.quantity === "") && (p.una_tantum || p.recurrence === "una_tantum")) return;
      const st = (p.start_date || ct.contract_start || "").slice(0, 10);
      const en = (p.end_date || ct.contract_end || "").slice(0, 10);
      if (st && st > endNext) return;
      if (en && en < startNext) return;
      amount += productMonthly(p, ct).monthlyNet;
    });
    if (amount > 0) rows.push({ client: c?.client_name || ct.client_id, source: "Contratto attivo", amount: Math.round(amount * 100) / 100 });
  });
  return { total: Math.round(rows.reduce((a, r) => a + r.amount, 0) * 100) / 100, rows, month: m, year: y };
};

// Le 8 card principali. Formule esclusivamente su dati canonici AOS.
export const dashboardCards = (period) => {
  const year = period?.year ?? REV_YEAR;
  const month = period?.month ?? CUR_MONTH;
  const cur = monthTotals(year, month);
  const prev = month === 1 ? monthTotals(year - 1, 12) : monthTotals(year, month - 1);
  const costs = costSummary(year, month).total;
  const deltaPct = prev.gross > 0 ? ((cur.gross - prev.gross) / prev.gross) * 100 : null;
  return {
    year, month,
    grossMonth: cur.gross,
    netMonth: cur.net,
    costsMonth: costs,
    marginGross: cur.gross - costs,
    marginNet: cur.net - costs,
    forecast: forecastNextMonth(year, month),
    deltaPct,
    prevGross: prev.gross,
    prevMonth: month === 1 ? 12 : month - 1,
    prevYear: month === 1 ? year - 1 : year,
    insoluti: insolutiTotal(),
  };
};

export const clientYearNet = (clientId) => read().revenue.filter((r) => r.client_id === clientId && r.year === REV_YEAR).reduce((a, r) => a + r.net_amount, 0);

export const accountingInsights = () => {
  const clients = getActiveRegistryClients();
  const s = read();
  const prodMap = {};
  s.revenue.filter((r) => r.year === REV_YEAR).forEach((r) => r.items.forEach((it) => { prodMap[it.product_service_name] = (prodMap[it.product_service_name] || 0) + it.net_amount; }));
  const bestProduct = Object.entries(prodMap).sort((a, b) => b[1] - a[1])[0];
  const byClient = clients.map((c) => ({ c, rev: clientYearNet(c.id), unpaid: s.revenue.filter((r) => r.client_id === c.id && ["unpaid", "overdue"].includes(r.payment_status)).length, hours: clientHours(c.id) }));
  const topSpender = [...byClient].sort((a, b) => b.rev - a.rev)[0];
  const mostUnpaid = [...byClient].sort((a, b) => b.unpaid - a.unpaid)[0];
  const withRatio = byClient.map((x) => ({ ...x, ratio: (x.rev / 12) / x.hours }));
  const bestRatio = [...withRatio].sort((a, b) => b.ratio - a.ratio)[0];
  const worstRatio = [...withRatio].sort((a, b) => a.ratio - b.ratio)[0];
  return { bestProduct: bestProduct ? { name: bestProduct[0], value: bestProduct[1] } : null, topSpender, mostUnpaid, bestRatio, worstRatio };
};

// Insights calcolati dalle VERE voci di fatturazione (endpoint /billing/overview),
// da agosto in poi. Il tempo per cliente è sommato dalle Operazioni mensili (tutte le
// risorse su ogni voce/campagna/extra sono già sommate da opTotalTime).
const clientHoursRange = (clientId, fromM, toM) => {
  let h = 0;
  for (let m = fromM; m <= toM; m++) {
    const op = listOps(REV_YEAR, m).find((o) => o.client_id === clientId);
    if (op) h += opTotalTime(op);
  }
  return Math.max(h, 1);
};
export const overviewInsights = (ov) => {
  if (!ov) return null;
  const fromM = ov.insights_from_month || 8;
  const toM = Math.max(CUR_MONTH, fromM);
  const withRatio = (ov.by_client || []).map((x) => {
    const hours = clientHoursRange(x.client_id, fromM, toM);
    return { c: { client_name: x.client_name, id: x.client_id }, rev: x.net, hours, ratio: x.net / hours };
  });
  const bestRatio = [...withRatio].sort((a, b) => b.ratio - a.ratio)[0] || null;
  const worstRatio = [...withRatio].sort((a, b) => a.ratio - b.ratio)[0] || null;
  return {
    bestProduct: ov.best_product ? { name: ov.best_product.name, value: ov.best_product.net } : null,
    bestRatio, worstRatio,
  };
};
export const seriesFromOverview = (ov, year) => {
  if (!ov?.series) return null;
  return ov.series.map((s) => ({ month: MONTHS_IT[s.month - 1].slice(0, 3), lordo: s.lordo, ricavi: s.netto, benchmark: costSummary(year, s.month).total }));
};


export const profitability = () => {
  const clients = getActiveRegistryClients();
  const rows = clients.map((c) => {
    const rev = clientYearNet(c.id) / 12;
    const hours = clientHours(c.id);
    const labor = hours * avgHourlyRate();
    const clientCosts = [...recurringCostsForMonth(CUR_MONTH), ...getOccasionalCosts(REV_YEAR, CUR_MONTH)].filter((x) => x.client_id === c.id).reduce((a, x) => a + x.amount, 0);
    const profit = rev - labor - clientCosts;
    return { c, rev, hours, labor, clientCosts, profit, ratio: rev / hours };
  });
  const byProfit = [...rows].sort((a, b) => b.profit - a.profit);
  const byRatio = [...rows].sort((a, b) => b.ratio - a.ratio);
  return { rows, most: byProfit[0], least: byProfit[byProfit.length - 1], bestRatio: byRatio[0], worstRatio: byRatio[byRatio.length - 1] };
};
